import { ButtonBase, Chip, IconButton } from "@mui/material";
import { DateTime } from "luxon";
import { createElement, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TbArrowMoveRight, TbClock, TbDownload, TbPlaneInflight } from "react-icons/tb";

import DriverAvatar from "@/components/DriverAvatar";
import { TaskStatuses } from "@/constants/status";
import { downloadUetdsPdf } from "@/helpers/download";
import { formatFlightNumber } from "@/helpers/strings";
import { PassengerContent } from "@/pages/WelcomeTeam/components/PassengerContent";
import { DriverType } from "@/types/driver.types";
import { IPlanPassenger, Task } from "@/types/tasks.types";
import { UserType } from "@/types/user.types";

import { TrackingStatusCell } from "../table/TrackingStatusColumn";
import MobileTrackingActions from "./MobileTrackingActions";
import MobileTrackingPassengerActions from "./MobileTrackingPassengerActions";

export type TrackingTaskCardProps = {
    task: Task<IPlanPassenger<"api">>;
    drivers: DriverType[];
};

function TrackingTaskCard({ task, drivers }: TrackingTaskCardProps) {
    const passenger = task.passengers[0];
    const driver = drivers.find((driver) => driver.id === task.driverId);
    const { t } = useTranslation();

    const handleDownloadUetdsPdf = useCallback(() => {
        downloadUetdsPdf(task.uetdsPdfUrl, task.id, t);
    }, [task.uetdsPdfUrl, task.id, t]);

    if (!passenger) return null;
    return (
        <div className="bg-white shadow-lg flex p-2 py-2 flex-col gap-3 text-sm relative    overflow-hidden">
            <div className="flex justify-between gap-1  items-center    ">
                {driver && <DriverAvatar blackIcon userType={UserType.DRIVER} avatar name={driver.name} />}
                <div className="flex items-center gap-1 ">
                    <span className="font-bold">{DateTime.now()?.toFormat("HH:mm")}</span>
                    <TbClock color="gray" className=" lg:text-lg col-span-2" />
                    <div className="col-span-10  flex gap-2 items-start"></div>
                </div>
                <div className="ml-auto">
                    <MobileTrackingActions task={task} />
                </div>
            </div>
            <div className=" grid items-center font-semibold px-2 py-1 rounded   gap-1  grid-cols-[1fr,30px,1fr] bg-gray-100">
                {task.origin.alias}
                <TbArrowMoveRight fontSize={27} className={"!shrink-0 !self-center duration-200 mx-auto "} />
                <span className="ml-auto">{task.destination.alias}</span>
            </div>
            <div className="flex flex-col gap-2">
                {task.passengers.map((passenger) => (
                    <ButtonBase key={passenger.id} component="div" className=" !grow">
                        <PassengerContent passenger={passenger}>
                            <div className="ml-auto   max-w-fit flex justify-end">
                                <Chip
                                    sx={{
                                        backgroundColor: "transparent",
                                        fontWeight: 500,
                                        fontSize: "12px !important",
                                    }}
                                    icon={createElement(TbPlaneInflight, {
                                        fontSize: 14,
                                    })}
                                    label={formatFlightNumber(passenger.flightNumber || "")}
                                />
                            </div>
                            <MobileTrackingPassengerActions
                                passenger={passenger}
                                totalPassengers={task.passengers.length}
                            />
                        </PassengerContent>
                    </ButtonBase>
                ))}
            </div>
            <div className=" -translate-x-1 flex justify-between">
                <TrackingStatusCell value={task.status as TaskStatuses} id={task.id} />
                {task.uetdsId && (
                    <IconButton
                        onClick={handleDownloadUetdsPdf}
                        size="small"
                        className=" !w-[30px] !mr-1 grow-0 shrink-0 !h-[30px]"
                    >
                        <TbDownload size={18} />
                    </IconButton>
                )}
            </div>
        </div>

        // <div className="bg-white shadow-lg flex flex-col rounded-3xl py-4 p-2 gap-4">
        //     <div className="flex items-center gap-2 text-elitDark mx-2 text-sm">
        //         <TbUserFilled className="text-lg" /> {passenger.name}
        //     </div>
        //     {/* <TrackingTaskStatusCard task={task} passenger={passenger} /> */}
        //     <div className="flex items-center gap-2 text-elitDark mx-2">
        //         <div className="w-1/2 flex items-center gap-2 text-sm">
        //             <TbCar className="text-lg" /> <div>{task.driverName}</div>
        //         </div>
        //         <div
        //             className={`w-1/2 flex items-center justify-between rounded-full text-white px-4 py-1 text-sm ${
        //                 passenger.isFlightArrival ? "bg-green-500" : "bg-blue-500"
        //             }`}
        //         >
        //             <TbPlaneArrival />
        //             <span className="uppercase">{passenger.flightNumber}</span>{" "}
        //             <span>{passenger.flightTime?.toFormat("HH:mm")}</span>
        //         </div>
        //     </div>
        // </div>
    );
}

export default TrackingTaskCard;
